import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { callClaude } from '../claude'

const DIMENSIONS = [
  {
    key: 'customer_relationship',
    label: 'Customer Relationship & Intelligence',
    weight: 0.25,
    guidance: 'How well do you know the buyer, their priorities, and their hot buttons? Have you engaged pre-ITT?'
  },
  {
    key: 'competitive_position',
    label: 'Competitive Position',
    weight: 0.25,
    guidance: 'How do you rate against likely competitors? Do you have genuine discriminators? Is there an incumbent to displace?'
  },
  {
    key: 'solution_readiness',
    label: 'Solution Readiness & Fit',
    weight: 0.20,
    guidance: 'Does your solution meet the requirement? Do you have relevant past performance? Can you resource and mobilise?'
  },
  {
    key: 'commercial_attractiveness',
    label: 'Commercial & Strategic Attractiveness',
    weight: 0.20,
    guidance: 'Is the contract value attractive? Does it fit your strategy? Are terms acceptable? Is there growth potential?'
  },
  {
    key: 'bid_resource',
    label: 'Bid Resource & Deliverability',
    weight: 0.10,
    guidance: 'Do you have the bid team, timeline, executive sponsorship, and budget to pursue this properly?'
  }
]

function getRecommendation(score) {
  if (score >= 3.75) return 'pursue'
  if (score >= 2.5) return 'conditional'
  return 'no_bid'
}

function getRecommendationLabel(rec) {
  if (rec === 'pursue') return 'PURSUE'
  if (rec === 'conditional') return 'CONDITIONAL PURSUE'
  return 'NO BID'
}

const REC_CONFIG = {
  pursue:      { label: 'PURSUE',      bg: '2D6A4F' },
  conditional: { label: 'CONDITIONAL', bg: 'C9A84C' },
  no_bid:      { label: 'NO BID',      bg: 'C0392B' },
}

function Qualification({ session, profile, onNavigate, preloadedOpportunity, existingScore, onScoreSaved }) {
  const [listView, setListView] = useState(!preloadedOpportunity)
  const [scoresList, setScoresList] = useState([])
  const [loadingList, setLoadingList] = useState(false)
  const [step, setStep] = useState(existingScore ? 'result' : 'details')
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [generatingNarrative, setGeneratingNarrative] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  const [details, setDetails] = useState({
    title: preloadedOpportunity?.title || '',
    buyer: preloadedOpportunity?.buyer || '',
    contract_value_min: preloadedOpportunity?.contract_value_min || '',
    contract_value_max: preloadedOpportunity?.contract_value_max || '',
    deadline: preloadedOpportunity?.deadline
      ? new Date(preloadedOpportunity.deadline).toISOString().split('T')[0]
      : '',
    framework: preloadedOpportunity?.framework || '',
    description: preloadedOpportunity?.description || ''
  })

  const [scores, setScores] = useState({
    customer_relationship_score: existingScore?.customer_relationship_score || 3,
    competitive_position_score: existingScore?.competitive_position_score || 3,
    solution_readiness_score: existingScore?.solution_readiness_score || 3,
    commercial_attractiveness_score: existingScore?.commercial_attractiveness_score || 3,
    bid_resource_score: existingScore?.bid_resource_score || 3
  })

  const [notes, setNotes] = useState({
    customer_relationship_notes: existingScore?.customer_relationship_notes || '',
    competitive_position_notes: existingScore?.competitive_position_notes || '',
    solution_readiness_notes: existingScore?.solution_readiness_notes || '',
    commercial_attractiveness_notes: existingScore?.commercial_attractiveness_notes || '',
    bid_resource_notes: existingScore?.bid_resource_notes || ''
  })

  const [pwin, setPwin] = useState({
    pwin_estimate: existingScore?.pwin_estimate || '',
    pwin_minimum: existingScore?.pwin_minimum || '',
    pwin_adjusted: existingScore?.pwin_adjusted || ''
  })

  const [qualitative, setQualitative] = useState({
    biggest_risk: existingScore?.biggest_risk || '',
    most_important_action: existingScore?.most_important_action || '',
    ethical_concerns: existingScore?.ethical_concerns || '',
    conditional_actions: existingScore?.conditional_actions || '',
    executive_summary: existingScore?.executive_summary || ''
  })

  useEffect(() => {
    if (listView) loadScoresList()
  }, [listView])

  const loadScoresList = async () => {
    setLoadingList(true)
    const { data } = await supabase
      .from('qualification_scores')
      .select(`
        *,
        opportunities (id, title, buyer, deadline, stage)
      `)
      .order('created_at', { ascending: false })
    setScoresList(data || [])
    setLoadingList(false)
  }

  const overallScore = DIMENSIONS.reduce((total, dim) => {
    const score = scores[`${dim.key}_score`] || 0
    return total + (score * dim.weight)
  }, 0)

  const recommendation = getRecommendation(overallScore)

  const handleGenerateNarrative = async () => {
    if (!profile?.organisations?.ai_enabled) return
    setGeneratingNarrative(true)
    try {
      const opp = preloadedOpportunity
      const system = 'You are an expert bid qualification manager writing for a senior audience. Write a single flowing paragraph of exactly 2-3 sentences summarising the qualification decision. No bullet points, no headers, no preamble. Start directly with the recommendation. Be commercially precise and specific to the scores provided.'
      const prompt = `Write a qualification narrative for this opportunity.

Opportunity: ${opp?.title || details.title}
Buyer: ${opp?.buyer || details.buyer}
Overall Score: ${overallScore.toFixed(0)}%
Recommendation: ${recommendation.toUpperCase()}

Dimension Scores (out of 5):
- Customer Relationship: ${scores.customer_relationship_score}/5${notes.customer_relationship_notes ? ' — ' + notes.customer_relationship_notes : ''}
- Competitive Position: ${scores.competitive_position_score}/5${notes.competitive_position_notes ? ' — ' + notes.competitive_position_notes : ''}
- Solution Readiness: ${scores.solution_readiness_score}/5${notes.solution_readiness_notes ? ' — ' + notes.solution_readiness_notes : ''}
- Commercial Attractiveness: ${scores.commercial_attractiveness_score}/5${notes.commercial_attractiveness_notes ? ' — ' + notes.commercial_attractiveness_notes : ''}
- Bid Resource: ${scores.bid_resource_score}/5${notes.bid_resource_notes ? ' — ' + notes.bid_resource_notes : ''}

${qualitative.biggest_risk ? 'Biggest Risk: ' + qualitative.biggest_risk : ''}
${qualitative.most_important_action ? 'Most Important Action: ' + qualitative.most_important_action : ''}

Write a 2-3 sentence executive summary that captures the qualification decision, the key rationale, and any critical conditions or actions required.`

      const result = await callClaude({ system, prompt, max_tokens: 300 })
      setQualitative(prev => ({ ...prev, executive_summary: result.trim() }))
    } catch (err) {
      console.error('Narrative error:', err)
    }
    setGeneratingNarrative(false)
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      let opp
      if (preloadedOpportunity) {
        opp = preloadedOpportunity
        await supabase
          .from('opportunities')
          .update({
            stage: recommendation === 'no_bid' ? 'no_bid' : 'pursuing',
            status: recommendation === 'no_bid' ? 'no_bid' : 'pursuing'
          })
          .eq('id', opp.id)
      } else {
        const { data: newOpp, error: oppError } = await supabase
          .from('opportunities')
          .insert([{
            title: details.title,
            buyer: details.buyer,
            contract_value_min: details.contract_value_min || null,
            contract_value_max: details.contract_value_max || null,
            deadline: details.deadline || null,
            framework: details.framework || null,
            description: details.description || null,
            stage: recommendation === 'no_bid' ? 'no_bid' : 'pursuing',
            status: recommendation === 'no_bid' ? 'no_bid' : 'qualifying',
            organisation_id: profile?.organisation_id || null,
            created_by: session.user.id
          }])
          .select()
          .single()
        if (oppError) throw oppError
        opp = newOpp
      }

      let scoreData, scoreError

      if (existingScore?.id && isEditing) {
        const { data, error } = await supabase
          .from('qualification_scores')
          .update({
            ...scores,
            ...notes,
            ...pwin,
            ...qualitative,
            overall_score: overallScore.toFixed(2),
            recommendation,
          })
          .eq('id', existingScore.id)
          .select()
          .single()
        scoreData = data
        scoreError = error
      } else {
        const { data, error } = await supabase
          .from('qualification_scores')
          .insert([{
            opportunity_id: opp.id,
            ...scores,
            ...notes,
            ...pwin,
            ...qualitative,
            overall_score: overallScore.toFixed(2),
            recommendation,
            assessed_by: session.user.id
          }])
          .select()
          .single()
        scoreData = data
        scoreError = error
      }

      if (scoreError) throw scoreError

      setSaved(true)
      if (onScoreSaved) onScoreSaved(scoreData)
      setStep('result')

    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // ── List view ──────────────────────────────────────────────────────────────
  if (listView) {
    return (
      <div className="scorecard-page container">
        <div className="page-header">
          <p className="label">Bid Qualification</p>
          <h1>Qualification Scorecards</h1>
          <p>All completed bid qualification assessments. Start a new one or review an existing scorecard.</p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <button
            className="btn btn--primary"
            onClick={() => setListView(false)}
          >
            + New Qualification
          </button>
        </div>

        {loadingList && <p style={{ color: 'var(--slate)' }}>Loading scorecards...</p>}

        {!loadingList && scoresList.length === 0 && (
          <div className="card">
            <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>
              No qualifications yet. Click <strong>+ New Qualification</strong> to assess your first opportunity.
            </p>
          </div>
        )}

        {scoresList.map(score => {
          const rec = REC_CONFIG[score.recommendation]
          const opp = score.opportunities
          return (
            <div
              key={score.id}
              className="card"
              style={{ cursor: 'pointer', marginBottom: '12px', transition: 'box-shadow 0.2s' }}
              onClick={() => {
                setDetails({ title: score.opportunities?.title || '', buyer: score.opportunities?.buyer || '', contract_value_min: '', contract_value_max: '', deadline: '', framework: '', description: '' })
                setScores({ customer_relationship_score: score.customer_relationship_score, competitive_position_score: score.competitive_position_score, solution_readiness_score: score.solution_readiness_score, commercial_attractiveness_score: score.commercial_attractiveness_score, bid_resource_score: score.bid_resource_score })
                setNotes({ customer_relationship_notes: score.customer_relationship_notes || '', competitive_position_notes: score.competitive_position_notes || '', solution_readiness_notes: score.solution_readiness_notes || '', commercial_attractiveness_notes: score.commercial_attractiveness_notes || '', bid_resource_notes: score.bid_resource_notes || '' })
                setPwin({ pwin_estimate: score.pwin_estimate || '', pwin_minimum: score.pwin_minimum || '', pwin_adjusted: score.pwin_adjusted || '' })
                setQualitative({ biggest_risk: score.biggest_risk || '', most_important_action: score.most_important_action || '', ethical_concerns: score.ethical_concerns || '', conditional_actions: score.conditional_actions || '', executive_summary: score.executive_summary || '' })
                setListView(false)
                setStep('result')
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(45,106,79,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    {rec && (
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 700,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        color: 'var(--white)', background: `#${rec.bg}`,
                        padding: '3px 8px'
                      }}>
                        {rec.label}
                      </span>
                    )}
                    {score.pwin_adjusted && (
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 600,
                        color: 'var(--green)', background: 'var(--green-pale)',
                        padding: '3px 8px'
                      }}>
                        PWin {score.pwin_adjusted}%
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: '1.25rem', fontWeight: 600,
                    color: 'var(--green-dark)', marginBottom: '4px'
                  }}>
                    {opp?.title || 'Unknown Opportunity'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--slate)' }}>
                    {opp?.buyer}
                    {opp?.deadline && ` · Deadline: ${new Date(opp.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: '2.5rem', fontWeight: 700,
                    color: 'var(--green-dark)', lineHeight: 1
                  }}>
                    {parseFloat(score.overall_score).toFixed(2)}
                  </div>
                  <div style={{
                    fontSize: '0.625rem', fontWeight: 600,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'var(--slate)'
                  }}>
                    out of 5.00
                  </div>
                </div>
              </div>
            </div>
          )
        })}

        <div className="actions">
          <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>
            ← Dashboard
          </button>
        </div>
      </div>
    )
  }

  // ── Step 1: Opportunity Details ──────────────────────────────────────────
  if (step === 'details') {
    return (
      <div className="scorecard-page container">
        <div className="page-header">
          <p className="label">Bid Qualification</p>
          <h1>New Opportunity Assessment</h1>
          <p>Complete the opportunity details then score across the five qualification dimensions.</p>
        </div>

        <div className="card">
          <div className="card__header">
            <span className="card__title">Opportunity Details</span>
            <span className="card__weight">Step 1 of 3</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label>Opportunity / Bid Title *</label>
              <input
                type="text"
                value={details.title}
                onChange={e => setDetails({ ...details, title: e.target.value })}
                placeholder="e.g. HMRC Digital Transformation Programme"
              />
            </div>
            <div className="form-group">
              <label>Client / Buyer Organisation *</label>
              <input
                type="text"
                value={details.buyer}
                onChange={e => setDetails({ ...details, buyer: e.target.value })}
                placeholder="e.g. HM Revenue & Customs"
              />
            </div>
            <div className="form-group">
              <label>Framework (if applicable)</label>
              <input
                type="text"
                value={details.framework}
                onChange={e => setDetails({ ...details, framework: e.target.value })}
                placeholder="e.g. TS4 RM6190 Lot 2"
              />
            </div>
            <div className="form-group">
              <label>Estimated Value Min (£)</label>
              <input
                type="number"
                value={details.contract_value_min}
                onChange={e => setDetails({ ...details, contract_value_min: e.target.value })}
                placeholder="e.g. 5000000"
              />
            </div>
            <div className="form-group">
              <label>Estimated Value Max (£)</label>
              <input
                type="number"
                value={details.contract_value_max}
                onChange={e => setDetails({ ...details, contract_value_max: e.target.value })}
                placeholder="e.g. 10000000"
              />
            </div>
            <div className="form-group">
              <label>Submission Deadline</label>
              <input
                type="date"
                value={details.deadline}
                onChange={e => setDetails({ ...details, deadline: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label>Opportunity Summary</label>
              <textarea
                value={details.description}
                onChange={e => setDetails({ ...details, description: e.target.value })}
                placeholder="Brief description of the requirement, scope, and any known context."
                rows={4}
              />
            </div>
          </div>
        </div>

        <div className="actions">
          <button
            className="btn btn--primary"
            onClick={() => setStep('scores')}
            disabled={!details.title || !details.buyer}
          >
            Next — Score the Opportunity →
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => preloadedOpportunity ? onNavigate('opportunities') : setListView(true)}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // ── Step 2: Scores ──────────────────────────────────────────────────────
  if (step === 'scores') {
    return (
      <div className="scorecard-page container">
        <div className="page-header">
          <p className="label">Bid Qualification</p>
          <h1>{details.title}</h1>
          <p>{details.buyer} — Score each dimension honestly. 1 = very weak, 5 = very strong.</p>
        </div>

        {DIMENSIONS.map(dim => {
          const scoreKey = `${dim.key}_score`
          const notesKey = `${dim.key}_notes`
          const currentScore = scores[scoreKey]
          const weightedScore = currentScore * dim.weight

          return (
            <div className="card" key={dim.key}>
              <div className="card__header">
                <span className="card__title">{dim.label}</span>
                <span className="card__weight">Weight: {Math.round(dim.weight * 100)}%</span>
              </div>

              <div className="dimension__guidance">{dim.guidance}</div>

              <div className="score-buttons">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    className={`score-btn ${currentScore === n ? 'selected' : ''}`}
                    onClick={() => setScores({ ...scores, [scoreKey]: n })}
                  >
                    {n}
                  </button>
                ))}
                <span style={{ marginLeft: '12px', fontSize: '0.875rem', color: 'var(--slate)', alignSelf: 'center' }}>
                  Weighted: {weightedScore.toFixed(2)}
                </span>
              </div>

              <div className="score-bar-wrap">
                <div className="score-bar" style={{ width: `${(currentScore / 5) * 100}%` }} />
              </div>

              <div className="form-group">
                <label>Notes / Evidence for this score</label>
                <textarea
                  value={notes[notesKey]}
                  onChange={e => setNotes({ ...notes, [notesKey]: e.target.value })}
                  placeholder="Record the reasoning behind your score — what intelligence supports it?"
                  rows={2}
                />
              </div>
            </div>
          )
        })}

        <div className="overall-score">
          <div>
            <div className="overall-score__label">Overall Qualification Score</div>
            <div className="overall-score__num">{overallScore.toFixed(2)}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--green-light)', marginTop: '4px' }}>out of 5.00</div>
          </div>
          <div>
            <div className="overall-score__label" style={{ marginBottom: '8px' }}>Recommendation</div>
            <div className={`recommendation-badge ${recommendation}`}>
              {getRecommendationLabel(recommendation)}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--silver)', marginTop: '8px', maxWidth: '220px' }}>
              {recommendation === 'pursue' && 'Score ≥ 3.75 — strong case to pursue'}
              {recommendation === 'conditional' && 'Score 2.50–3.74 — pursue with conditions'}
              {recommendation === 'no_bid' && 'Score < 2.50 — recommend no bid'}
            </div>
          </div>
        </div>

        <div className="actions">
          <button className="btn btn--primary" onClick={() => setStep('qualitative')}>
            Next — Qualitative Assessment →
          </button>
          <button className="btn btn--secondary" onClick={() => setStep('details')}>
            ← Back
          </button>
        </div>
      </div>
    )
  }

  // ── Step 3: Qualitative + PWin ──────────────────────────────────────────
  if (step === 'qualitative') {
    return (
      <div className="scorecard-page container">
        <div className="page-header">
          <p className="label">Bid Qualification</p>
          <h1>Qualitative Assessment</h1>
          <p>Complete the honest assessment questions and PWin estimate.</p>
        </div>

        <div className="card">
          <div className="card__header">
            <span className="card__title">Probability of Win (PWin)</span>
            <span className="card__weight">Step 3 of 3</span>
          </div>
          <div className="pwin-grid">
            <div className="pwin-item">
              <label>Team PWin Estimate (%)</label>
              <input
                type="number" min="0" max="100"
                value={pwin.pwin_estimate}
                onChange={e => setPwin({ ...pwin, pwin_estimate: e.target.value })}
                placeholder="e.g. 35"
              />
            </div>
            <div className="pwin-item">
              <label>Minimum PWin to Justify Bid (%)</label>
              <input
                type="number" min="0" max="100"
                value={pwin.pwin_minimum}
                onChange={e => setPwin({ ...pwin, pwin_minimum: e.target.value })}
                placeholder="e.g. 20"
              />
            </div>
            <div className="pwin-item">
              <label>Adjusted PWin After Review (%)</label>
              <input
                type="number" min="0" max="100"
                value={pwin.pwin_adjusted}
                onChange={e => setPwin({ ...pwin, pwin_adjusted: e.target.value })}
                placeholder="e.g. 30"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__header">
            <span className="card__title">Key Questions</span>
          </div>
          {[
            { key: 'biggest_risk', label: 'Biggest Risk to Winning', placeholder: 'What is the single factor most likely to prevent us winning this bid?' },
            { key: 'most_important_action', label: 'Most Important Action', placeholder: 'What is the single most important action to improve our position?' },
            { key: 'ethical_concerns', label: 'Ethical / Reputational Concerns', placeholder: 'Are there any ethical, reputational, or policy concerns to flag?' },
            { key: 'conditional_actions', label: 'Conditions for Pursuit', placeholder: 'If conditional — what specific actions must be completed to confirm pursuit?' },
            { key: 'executive_summary', label: 'Executive Summary', placeholder: 'Summarise the qualification recommendation in 2–3 sentences for briefing upwards.' },
          ].map(q => (
            <div className="form-group" key={q.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <label style={{ margin: 0 }}>{q.label}</label>
                {q.key === 'executive_summary' && profile?.organisations?.ai_enabled && (
                  <button
                    type="button"
                    onClick={handleGenerateNarrative}
                    disabled={generatingNarrative}
                    style={{ background: 'none', border: '1px solid var(--green-dark)', color: 'var(--green-dark)', padding: '3px 12px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    {generatingNarrative ? '⏳ Generating...' : '✦ Generate'}
                  </button>
                )}
              </div>
              <textarea
                value={qualitative[q.key]}
                onChange={e => setQualitative({ ...qualitative, [q.key]: e.target.value })}
                placeholder={q.placeholder}
                rows={3}
              />
            </div>
          ))}
        </div>

        <div className="overall-score">
          <div>
            <div className="overall-score__label">Final Score</div>
            <div className="overall-score__num">{overallScore.toFixed(2)}</div>
          </div>
          <div>
            <div className={`recommendation-badge ${recommendation}`}>
              {getRecommendationLabel(recommendation)}
            </div>
          </div>
        </div>

        {error && <div className="error-msg" style={{ marginTop: '16px' }}>{error}</div>}

        <div className="actions">
          <button
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : isEditing ? 'Update Scorecard →' : 'Save Qualification →'}
          </button>
          <button className="btn btn--secondary" onClick={() => setStep('scores')}>
            ← Back
          </button>
        </div>
      </div>
    )
  }

  // ── Step 4: Result ──────────────────────────────────────────────────────
  if (step === 'result') {
    return (
      <div className="scorecard-page container">
        <div className="page-header">
          <p className="label">Qualification Complete</p>
          <h1>{details.title}</h1>
          <p>{details.buyer}</p>
        </div>

        <div className="overall-score" style={{ marginBottom: '24px' }}>
          <div>
            <div className="overall-score__label">Qualification Score</div>
            <div className="overall-score__num">{overallScore.toFixed(2)}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--green-light)', marginTop: '4px' }}>out of 5.00</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="overall-score__label" style={{ marginBottom: '8px' }}>Decision</div>
            <div className={`recommendation-badge ${recommendation}`} style={{ fontSize: '1.125rem', padding: '12px 28px' }}>
              {getRecommendationLabel(recommendation)}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card__title">Score Breakdown</span>
            {profile?.organisations?.ai_enabled && (
              <button
                onClick={handleGenerateNarrative}
                disabled={generatingNarrative}
                style={{ background: 'none', border: '1px solid var(--green-dark)', color: 'var(--green-dark)', padding: '4px 14px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {generatingNarrative ? '⏳ Generating...' : '✦ Generate AI Summary'}
              </button>
            )}
          </div>
          {qualitative.executive_summary && (
            <div style={{ background: 'var(--green-dark)', padding: '14px 18px', marginBottom: '16px' }}>
              <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '6px' }}>AI Qualification Summary</div>
              <div style={{ fontSize: '0.9375rem', color: 'var(--white)', lineHeight: 1.6 }}>{qualitative.executive_summary}</div>
            </div>
          )}
          {DIMENSIONS.map(dim => {
            const score = scores[`${dim.key}_score`]
            const weighted = score * dim.weight
            return (
              <div key={dim.key} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--charcoal)' }}>
                    {dim.label}
                  </span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--slate)' }}>
                    {score}/5 — weighted {weighted.toFixed(2)}
                  </span>
                </div>
                <div className="score-bar-wrap">
                  <div className="score-bar" style={{ width: `${(score / 5) * 100}%` }} />
                </div>
              </div>
            )
          })}
        </div>

        {qualitative.executive_summary && (
          <div className="card">
            <div className="card__header">
              <span className="card__title">Executive Summary</span>
            </div>
            <p style={{ color: 'var(--slate)', lineHeight: 1.7 }}>
              {qualitative.executive_summary}
            </p>
          </div>
        )}

      {saved && (
            <div style={{
              background: 'var(--green-pale)', padding: '20px',
              borderLeft: '4px solid var(--green)', marginBottom: '24px'
            }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--green-dark)', margin: 0 }}>
                ✓ {isEditing ? 'Scorecard updated successfully.' : 'Qualification saved successfully. This opportunity has been added to your pipeline.'}
              </p>
            </div>
          )}

        <div className="actions">
          <button
            className="btn btn--primary"
            onClick={() => {
              setIsEditing(true)
              setStep('details')
            }}
          >
            ✎ Edit Scorecard
          </button>
          {!existingScore && (
            <button
              className="btn btn--primary"
              onClick={() => {
                setStep('details')
                setDetails({ title: '', buyer: '', contract_value_min: '', contract_value_max: '', deadline: '', framework: '', description: '' })
                setScores({ customer_relationship_score: 3, competitive_position_score: 3, solution_readiness_score: 3, commercial_attractiveness_score: 3, bid_resource_score: 3 })
                setNotes({ customer_relationship_notes: '', competitive_position_notes: '', solution_readiness_notes: '', commercial_attractiveness_notes: '', bid_resource_notes: '' })
                setPwin({ pwin_estimate: '', pwin_minimum: '', pwin_adjusted: '' })
                setQualitative({ biggest_risk: '', most_important_action: '', ethical_concerns: '', conditional_actions: '', executive_summary: '' })
                setSaved(false)
                setListView(false)
              }}
            >
              Qualify Another Opportunity
            </button>
          )}
          <button
            className="btn btn--secondary"
            onClick={() => preloadedOpportunity ? onNavigate('opportunities') : setListView(true)}
          >
            {preloadedOpportunity ? '← Back to Pipeline' : 'View All Scorecards'}
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => onNavigate('dashboard')}
          >
            Dashboard
          </button>
        </div>
      </div>
    )
  }
}

export default Qualification